import React from "react";

// An animated illustration of the iOS contact-import flow. When a .vcf opens,
// iOS shows a contact preview and the user MUST tap "Create New Contact"
// (or "Add to Existing Contact") for the contact to actually be saved.
const ContactFlowAnimation = () => (
  <svg
    viewBox="0 0 280 300"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Animation showing how to save the contact on iPhone by tapping Create New Contact"
    className="w-48 h-auto mx-auto"
  >
    {/* Phone body */}
    <rect x="40" y="8" width="200" height="284" rx="34" fill="#ffffff" stroke="#e2e8f0" strokeWidth="3" />
    <rect x="46" y="14" width="188" height="272" rx="28" fill="#f1f5f9" />

    {/* Notch */}
    <rect x="112" y="20" width="56" height="14" rx="7" fill="#0f172a" />

    {/* Dimmed background app behind the sheet */}
    <rect x="46" y="40" width="188" height="246" rx="0" fill="#e2e8f0" opacity="0.6" />
    <rect x="68" y="58" width="90" height="10" rx="5" fill="#cbd5e1" />
    <rect x="68" y="78" width="140" height="8" rx="4" fill="#cbd5e1" />
    <rect x="68" y="94" width="120" height="8" rx="4" fill="#cbd5e1" />

    {/* iOS action sheet that slides up */}
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        from="0 260"
        to="0 0"
        begin="0s"
        dur="0.7s"
        fill="freeze"
        calcMode="spline"
        keySplines="0.16 1 0.3 1"
      />
      {/* Sheet card */}
      <rect x="52" y="118" width="176" height="168" rx="18" fill="#ffffff" />
      <rect x="106" y="126" width="68" height="5" rx="2.5" fill="#cbd5e1" />

      {/* Contact preview */}
      <circle cx="140" cy="158" r="18" fill="#d1fae5" />
      <path
        d="M140 158a7 7 0 100-14 7 7 0 000 14zm0 3c-6 0-11 3-11 7v2h22v-2c0-4-5-7-11-7z"
        fill="#059669"
      />
      <rect x="106" y="184" width="68" height="8" rx="4" fill="#0f172a" opacity="0.85" />
      <rect x="116" y="198" width="48" height="6" rx="3" fill="#94a3b8" />

      {/* Divider */}
      <rect x="64" y="216" width="152" height="1.5" fill="#e2e8f0" />

      {/* Action row: Create New Contact (highlighted on tap) */}
      <g>
        <rect x="64" y="222" width="152" height="30" rx="9" fill="#10b981">
          <animate
            attributeName="opacity"
            values="0;0;1;1;0"
            keyTimes="0;0.45;0.5;0.85;1"
            dur="2.4s"
            begin="0.7s"
            repeatCount="indefinite"
          />
        </rect>
        <text
          x="140"
          y="241"
          textAnchor="middle"
          fontFamily="-apple-system, system-ui, sans-serif"
          fontSize="11"
          fontWeight="600"
          fill="#059669"
        >
          Create New Contact
          <animate
            attributeName="fill"
            values="#059669;#059669;#ffffff;#ffffff;#059669"
            keyTimes="0;0.45;0.5;0.85;1"
            dur="2.4s"
            begin="0.7s"
            repeatCount="indefinite"
          />
        </text>
      </g>

      {/* Action row: Add to Existing Contact */}
      <text
        x="140"
        y="270"
        textAnchor="middle"
        fontFamily="-apple-system, system-ui, sans-serif"
        fontSize="11"
        fontWeight="500"
        fill="#64748b"
      >
        Add to Existing Contact
      </text>
    </g>

    {/* Tap ripple over the "Create New Contact" row */}
    <circle cx="140" cy="237" r="6" fill="none" stroke="#059669" strokeWidth="2">
      <animate attributeName="r" values="2;22" dur="2.4s" begin="0.9s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.6;0" dur="2.4s" begin="0.9s" repeatCount="indefinite" />
    </circle>

    {/* Tapping finger */}
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        values="0 6; 0 -2; 0 6"
        keyTimes="0;0.08;0.2"
        dur="2.4s"
        begin="0.7s"
        repeatCount="indefinite"
      />
      <path
        d="M158 236c0-3 2-5 4-5s4 2 4 5v9l4 1c3 1 4 3 4 6l-1 8c-1 4-4 6-8 6h-7c-3 0-5-1-7-4l-6-9c-1-2 0-5 2-6 2-1 4 0 5 1l1 1v-19c0-3 2-5 4-5z"
        fill="#fde68a"
        stroke="#0f172a"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const SaveContactInstructions = ({ onConfirm }) => {
  return (
    <div className="flex flex-col px-6 pt-2 pb-6">
      <div className="text-center">
        <ContactFlowAnimation />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-2 mb-2">
          One last step to save the contact
        </h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
          After the contact card opens, tap{" "}
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            "Create New Contact"
          </span>{" "}
          (or "Add to Existing Contact") to finish saving it to your phone.
        </p>
      </div>

      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 rounded-xl p-4 my-6 flex items-start gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.8}
          stroke="currentColor"
          className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
        <p className="text-sm text-emerald-800 dark:text-emerald-200">
          If you skip this step, the contact won't be saved to your phone.
        </p>
      </div>

      <button
        type="button"
        onClick={onConfirm}
        className="w-full py-4 rounded-xl bg-emerald-600 text-white font-semibold text-base shadow-sm hover:bg-emerald-700 transition-colors"
      >
        Great, save contact
      </button>
    </div>
  );
};

export default SaveContactInstructions;
